import axios from 'axios';

export type FastApiValidationIssue = {
  loc?: Array<string | number>;
  msg?: string;
  type?: string;
  ctx?: Record<string, unknown>;
};

type FastApiErrorResponse = {
  detail?: string | FastApiValidationIssue[];
};

export function extractDetail(err: unknown): string | undefined {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as FastApiErrorResponse | undefined)
      ?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const messages = detail
        .map((issue: FastApiValidationIssue) => issue?.msg)
        .filter(Boolean);
      if (messages.length > 0) return messages.join('; ');
    }
  }
  return undefined;
}

export function isValidationError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 422;
}

export function isConflictError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 409;
}

/**
 * Parse FastAPI 422 detail into a map of field path -> message.
 * e.g. loc ["body", "var_approach"] -> { var_approach: "..." }
 *      loc ["body", "gross_exposure_metric", "summation_method"]
 *        -> { "gross_exposure_metric.summation_method": "..." }
 */
export function extractValidationFieldErrors(
  err: unknown,
): Record<string, string> {
  if (!axios.isAxiosError(err)) return {};
  const detail = (err.response?.data as FastApiErrorResponse | undefined)
    ?.detail;
  if (!Array.isArray(detail)) return {};

  const fieldErrors: Record<string, string> = {};
  detail.forEach((issue: FastApiValidationIssue) => {
    if (!issue?.loc?.length || !issue.msg) return;
    const path = issue.loc
      .filter((part: string | number) => part !== 'body')
      .map(String)
      .join('.');
    if (!path) return;
    fieldErrors[path] = issue.msg;
  });
  return fieldErrors;
}
